/**
 * 画面が選んでいる言語を、読取契約が受け取る locale へ写す。
 *
 * i18next の言語は地域つき（`ja-JP`）にも、揃えていない言語（ブラウザが `de`）にもなる。
 * 契約の `locale` は `SPEC_LOCALES` の 3 値しか受けないため、どちらへ倒すかをこの 1 箇所で
 * 決める。画面ごとに正規化すると、地域つきの言語を渡した画面だけが既定言語の応答を受け取り、
 * 同じ操作でも画面によって表示言語が変わる。
 *
 * 言語一覧をここで宣言しない。`SpecLocaleSchema.options` から引くのは、揃える言語の一次表現が
 * 共通部の `SPEC_LOCALES` だからである（`locales.ts` の「入力スキーマ・MCP 書込・画面の言語
 * 切替が別々に言語一覧を持つと、経路ごとに揃う言語が変わる」）。言語を足したときに増えるのは
 * 各言語の自称 1 行だけで、足し忘れは `satisfies` が型エラーにする。
 *
 * @graph-stack spec-console
 * @graph-domain Infrastructure
 * @graph-business 画面の表示言語を仕様グラフ読取 API の locale へ正規化する。
 * @graph-connects none
 */
import { SpecLocaleSchema, type SpecLocale } from '@cortex/domain-spec-console';
import { useTranslation } from 'react-i18next';

/**
 * 読取が本文を返せる言語の一覧。言語メニューの並びでもある。
 * @graph-connects none
 */
export const SPEC_LOCALE_CODES: readonly SpecLocale[] = SpecLocaleSchema.options;

/**
 * 各言語の自称。
 *
 * 翻訳リソースへ入れずにここへ置く。「Tiếng Việt」はどの言語で画面を見ていても
 * 「Tiếng Việt」であり、言語を選ぶ人はまだ自分の読める言語に切り替えられていないためである。
 * ja / en / vi の 3 ファイルへ同じ綴りを写すと、3 表現が乖離しうる状態を作ることにもなる。
 * @graph-connects none
 */
export const SPEC_LOCALE_ENDONYMS = {
  ja: '日本語',
  en: 'English',
  vi: 'Tiếng Việt',
} as const satisfies Record<SpecLocale, string>;

/**
 * i18next の言語文字列を契約の locale へ写す。揃えていない言語なら `undefined`。
 *
 * `undefined` を返すのは、web 側で既定言語を決め直さないためである。既定は契約の
 * `LocaleInputShape` が原文 locale として持っており、ここで同じ値を書くと原文がどれかの
 * 2 表現になる。
 *
 * @graph-stack spec-console
 * @graph-domain Infrastructure
 * @graph-business i18next の言語文字列を仕様グラフ読取 API の locale へ正規化する。
 * @graph-connects none
 */
export function resolveSpecLocale(language: string | undefined): SpecLocale | undefined {
  if (language === undefined) return undefined;
  const base = language.split('-')[0];
  return SPEC_LOCALE_CODES.find((locale) => locale === base);
}

/**
 * いま画面が描かれている言語の locale。揃えていない言語なら `undefined`。
 *
 * `i18n.language`（検出した言語）ではなく `i18n.resolvedLanguage`（実際に翻訳リソースを
 * 引いた言語）を見る。前者は未対応の言語をそのまま持つため、画面が fallback で日本語を
 * 描いているのに API へ未対応の言語を送る組み合わせが起きる。
 *
 * @graph-stack spec-console
 * @graph-domain Infrastructure
 * @graph-business 画面が描かれている言語を仕様グラフ読取 API の locale として供給する。
 * @graph-connects none
 */
export function useSpecLocale(): SpecLocale | undefined {
  const { i18n } = useTranslation();
  return resolveSpecLocale(i18n.resolvedLanguage);
}
